import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, Card, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import { useTranslation } from "react-i18next";
import {
  getStoreById,
  uploadStoreCarouselImage,
  uploadStoreCarouselVideo,
  deleteStoreCarouselItem,
} from "../../api/storeService";

const MAX_ITEMS = 3;

export default function StoreCarousel() {
  const { t } = useTranslation(["boStoreCarousel", "boShared"]);
  const { storeId } = useParams();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  function load() {
    setLoading(true);
    setError("");
    getStoreById(storeId)
      .then((store) => setItems(store.carouselItems || []))
      .catch(() => setError(t("boStoreCarousel:loadError")))
      .finally(() => setLoading(false));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [storeId]);

  async function handleImageChange(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const updated = await uploadStoreCarouselImage(storeId, file);
      setItems(updated.carouselItems || []);
      setNotice(t("boStoreCarousel:imageAdded"));
    } catch (err) {
      setError(err.response?.data || t("boStoreCarousel:imageUploadError"));
    } finally {
      setUploading(false);
    }
  }

  async function handleVideoChange(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const updated = await uploadStoreCarouselVideo(storeId, file);
      setItems(updated.carouselItems || []);
      setNotice(t("boStoreCarousel:videoAdded"));
    } catch (err) {
      setError(err.response?.data || t("boStoreCarousel:videoUploadError"));
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(itemId) {
    setDeletingId(itemId);
    setError("");
    try {
      const updated = await deleteStoreCarouselItem(storeId, itemId);
      setItems(updated.carouselItems || []);
    } catch {
      setError(t("boStoreCarousel:removeError"));
    } finally {
      setDeletingId(null);
    }
  }

  const canAddMore = items.length < MAX_ITEMS;

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate(`/business-owner/stores/${storeId}/layout`)} sx={{ mb: 0.5 }}>
        {t("boStoreCarousel:backToLayout")}
      </Button>

      <Typography variant="h4" sx={{ mb: 0.5 }}>
        {t("boStoreCarousel:title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("boStoreCarousel:subtitle", { max: MAX_ITEMS, used: items.length })}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {typeof error === "string" ? error : t("boShared:somethingWentWrong")}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={2}>
          {items.map((item) => (
            <Card key={item.itemId} variant="outlined" sx={{ width: 220, position: "relative", overflow: "hidden" }}>
              <Box sx={{ position: "relative", height: 140, bgcolor: "action.hover" }}>
                {item.mediaType === "Video" ? (
                  <>
                    <Box component="video" src={item.mediaUrl} sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} muted />
                    <PlayCircleOutlineRoundedIcon
                      sx={{ position: "absolute", inset: 0, m: "auto", fontSize: 40, color: "rgba(255,255,255,0.9)" }}
                    />
                  </>
                ) : (
                  <Box component="img" src={item.mediaUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                )}
                <IconButton
                  size="small"
                  onClick={() => handleDelete(item.itemId)}
                  disabled={deletingId === item.itemId}
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    bgcolor: "rgba(15, 23, 42, 0.65)",
                    color: "#fff",
                    "&:hover": { bgcolor: "rgba(190, 30, 30, 0.85)" },
                  }}
                >
                  {deletingId === item.itemId ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <DeleteOutlineRoundedIcon fontSize="small" />}
                </IconButton>
              </Box>
            </Card>
          ))}

          {canAddMore && (
            <Card
              variant="outlined"
              sx={{
                width: 220,
                height: 140,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                borderStyle: "dashed",
              }}
            >
              {uploading ? (
                <CircularProgress size={22} />
              ) : (
                <Stack direction="row" spacing={1}>
                  <Button size="small" startIcon={<AddPhotoAlternateOutlinedIcon />} onClick={() => imageInputRef.current?.click()}>
                    {t("boStoreCarousel:image")}
                  </Button>
                  <Button size="small" startIcon={<VideoLibraryOutlinedIcon />} onClick={() => videoInputRef.current?.click()}>
                    {t("boStoreCarousel:video")}
                  </Button>
                </Stack>
              )}
              <input ref={imageInputRef} type="file" accept="image/*" hidden onChange={handleImageChange} disabled={uploading} />
              <input ref={videoInputRef} type="file" accept="video/*" hidden onChange={handleVideoChange} disabled={uploading} />
            </Card>
          )}
        </Stack>
      )}
    </Box>
  );
}
